import { useCallback, useEffect, useRef } from "react";
import { Capacitor } from "@capacitor/core";
import { postEvent, useNativeMessagingContext } from "@WahooFitness/wahoo-offline-mfe";

export const REQUEST_RECOVERY_HEALTH_SCOPES_EVENT = "requestRecoveryHealthScopes";

// Bounds a host that never answers (no handler), not the user's time on the sheet; the toggle
// proceeds on timeout.
export const RECOVERY_HEALTH_SCOPES_TIMEOUT_MS = 60_000;

// Native's reply. Carries no grant state; HealthKit never discloses read authorization.
export type RecoveryHealthScopesOutcome = {
  available: boolean;
  requested: boolean;
  completed: boolean;
};

type Settle = (outcome: RecoveryHealthScopesOutcome | null) => void;

// Asks native to show the HealthKit sheet for still-unasked recovery scopes; resolves once it
// closes, null on web/timeout/malformed reply.
const useRecoveryHealthScopesRequest = () => {
  const { addEventResponseListener, removeEventResponseListener } = useNativeMessagingContext();
  const pending = useRef<Settle | null>(null);

  // Registered for the hook's lifetime: a listener added at request time could miss a fast
  // "nothing left to ask" reply.
  useEffect(() => {
    const listener = addEventResponseListener(
      REQUEST_RECOVERY_HEALTH_SCOPES_EVENT,
      ({ eventResponseData }) => {
        const settle = pending.current;
        pending.current = null;
        settle?.(isOutcome(eventResponseData) ? eventResponseData : null);
      }
    );
    return () => {
      removeEventResponseListener(listener);
    };
  }, [addEventResponseListener, removeEventResponseListener]);

  const requestRecoveryHealthScopes =
    useCallback((): Promise<RecoveryHealthScopesOutcome | null> => {
      if (Capacitor.getPlatform() === "web") {
        return Promise.resolve(null);
      }
      return new Promise((resolve) => {
        const settle: Settle = (outcome) => {
          clearTimeout(timer);
          resolve(outcome);
        };
        const timer = setTimeout(() => {
          if (pending.current === settle) {
            pending.current = null;
            resolve(null);
          }
        }, RECOVERY_HEALTH_SCOPES_TIMEOUT_MS);
        pending.current = settle;
        postEvent(REQUEST_RECOVERY_HEALTH_SCOPES_EVENT, {});
      });
    }, []);

  return { requestRecoveryHealthScopes };
};

const isOutcome = (value: unknown): value is RecoveryHealthScopesOutcome =>
  typeof value === "object" &&
  value !== null &&
  typeof (value as RecoveryHealthScopesOutcome).completed === "boolean";

export default useRecoveryHealthScopesRequest;
